import { Link } from "react-router-dom";
import {
  Instagram, Facebook, Linkedin, ChevronRight, Eye, Heart, MessageCircle,
  TrendingUp, Clock, Users, Target, Image as ImageIcon, Inbox, FolderOpen, FileText,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip as RTooltip } from "recharts";
import { SafeChart } from "@/components/SafeChart";

export const PASTEL = {
  blue: { bg: "#E0F2FE", text: "#0369A1", border: "#BAE6FD" },
  amber: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  purple: { bg: "#F3E8FF", text: "#6B21A8", border: "#E9D5FF" },
  rose: { bg: "#FFE4E6", text: "#BE123C", border: "#FECDD3" },
  green: { bg: "#DCFCE7", text: "#15803D", border: "#BBF7D0" },
};
export const NET_COLORS = { instagram: "#E1306C", facebook: "#1877F2", linkedin: "#0A66C2", tiktok: "#0F172A" };
export const NET_LABELS = { instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn", tiktok: "TikTok" };
export const SERVICE_LABELS = {
  community_management: "Community Management", communication: "Community Management",
  les_deux: "Community Management", cm: "Community Management", graphisme: "Graphisme",
  site_web: "Web Design", maintenance_web: "Web Design", seo: "SEO", publicite: "Publicité",
  newsletter: "Newsletter", autre: "Autres",
};
export const REPORT_STATUSES = {
  brouillon: { label: "Brouillon", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
  genere: { label: "Généré", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  valide: { label: "Validé", cls: "bg-green-50 text-green-700 border-green-200" },
  publie: { label: "Publié au client", cls: "bg-violet-50 text-violet-700 border-violet-200" },
};
export const PERIOD_OPTIONS = [
  ["month", "Ce mois"], ["prev_month", "Mois précédent"], ["3m", "3 mois"],
  ["6m", "6 mois"], ["12m", "12 mois"], ["custom", "Personnalisé"],
];
const BADGE_STYLES = {
  "Meilleure performance": "bg-green-100 text-green-700",
  "Forte portée": "bg-sky-100 text-sky-700",
  "Bon engagement": "bg-blue-50 text-blue-600",
  "Engagement élevé": "bg-amber-100 text-amber-700",
  "Régulier": "bg-neutral-100 text-neutral-500",
};

export const fmtN = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  n = Number(n);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1e6).toFixed(1).replace(".", ",")} M`;
  return Math.round(n).toLocaleString("fr-FR");
};
export const fmtPct = (n) => (n == null ? "—" : `${String(n).replace(".", ",")} %`);
export const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
export const relWhen = (iso) => {
  if (!iso) return "";
  try {
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 60) return `il y a ${mins} min`;
    const h = Math.round(mins / 60);
    if (h < 24) return `il y a ${h} h`;
    return `il y a ${Math.round(h / 24)} j`;
  } catch { return ""; }
};

export function NetIcon({ net, className = "w-4 h-4" }) {
  if (net === "instagram") return <Instagram className={className} style={{ color: NET_COLORS.instagram }} />;
  if (net === "facebook") return <Facebook className={className} style={{ color: NET_COLORS.facebook }} />;
  if (net === "linkedin") return <Linkedin className={className} style={{ color: NET_COLORS.linkedin }} />;
  return <span className={`${className} inline-flex items-center justify-center font-black text-[10px] text-[#0F172A]`}>TT</span>;
}

export function DeltaBadge({ value, suffix = "vs période précédente", testid }) {
  if (value == null) return null;
  const pos = value >= 0;
  return (
    <span className="flex items-center gap-1" data-testid={testid}>
      <span className={`text-[11px] font-semibold ${pos ? "text-[#15803D]" : "text-[#BE123C]"}`}>
        {pos ? "↗" : "↘"} {pos ? "+" : ""}{value} %
      </span>
      {suffix && <span className="text-[10px] text-neutral-400">{suffix}</span>}
    </span>
  );
}

export function AnCard({ icon: Icon, title, link, linkLabel, count, testid, className = "", action, children }) {
  return (
    <div className={`bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden flex flex-col ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100">
        <Icon className="w-4 h-4 text-[#0F172A]" strokeWidth={2} />
        <h3 className="font-heading font-bold text-[13px] text-[#0F172A]">{title}</h3>
        {count != null && <span className="text-[10px] text-neutral-400 bg-neutral-100 rounded-full px-1.5 py-0.5">{count}</span>}
        {action}
        {link && (
          <Link to={link} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline flex items-center gap-0.5 shrink-0" data-testid={`${testid}-see-all`}>
            {linkLabel || "Voir tout"} <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

export function AnKpi({ testid, icon: Icon, pastel, value, label, delta, sub, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag onClick={onClick} data-testid={testid}
      className={`bg-white border border-[#E2E8F0] rounded-2xl shadow-sm px-3.5 py-3 flex gap-3 items-center text-left w-full ${onClick ? "hover:border-[#94A3B8] transition-colors" : ""}`}>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center border shrink-0"
        style={{ background: pastel.bg, color: pastel.text, borderColor: pastel.border }}>
        <Icon className="w-4 h-4" strokeWidth={1.8} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-medium text-[#475569] truncate">{label}</p>
        <p className="font-heading text-xl font-bold text-[#0F172A] leading-tight">{value}</p>
        {delta != null ? <DeltaBadge value={delta} /> : (sub && <p className="text-[10px] text-neutral-400">{sub}</p>)}
      </div>
    </Tag>
  );
}

export function Spark({ data, color = "#0369A1", w = 64, h = 22 }) {
  if (!data?.length) return null;
  const max = Math.max(...data), min = Math.min(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1 || 1)) * w},${h - 2 - ((v - min) / span) * (h - 4)}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function Donut({ data, centerValue, centerLabel, testid, height = 170 }) {
  const COLORS = ["#0369A1", "#6B21A8", "#15803D", "#B45309", "#BE123C", "#94A3B8", "#0F172A"];
  return (
    <div data-testid={testid}>
      <div className="relative">
        <SafeChart height={height}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" innerRadius={52} outerRadius={72} paddingAngle={3}>
              {data.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <RTooltip formatter={(v, n) => [v, n]} />
          </PieChart>
        </SafeChart>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <p className="font-heading text-xl font-bold text-[#0F172A]">{centerValue}</p>
          <p className="text-[10px] text-neutral-400">{centerLabel}</p>
        </div>
      </div>
      <div className="px-4 pb-3 pt-1 space-y-1">
        {data.map((d, i) => (
          <div key={d.id || d.label} className="flex items-center gap-2 text-[11px]">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="text-neutral-600 flex-1 truncate">{d.label}</span>
            <span className="font-semibold text-[#0F172A]">{fmtN(d.value)}</span>
            <span className="text-neutral-400 w-9 text-right">({d.pct} %)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TopPubs({ items, onOpen, testid, dense = false }) {
  if (!items?.length) return <p className="px-4 py-6 text-[13px] text-neutral-400">Aucune publication sur la période.</p>;
  return (
    <div data-testid={testid}>
      {items.map((p) => (
        <button key={p.id} onClick={() => onOpen?.(p)} data-testid={`${testid}-item-${p.id}`}
          className={`w-full flex items-center gap-3 px-4 ${dense ? "py-2" : "py-2.5"} border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left`}>
          {(p.visual_preview_url || p.visual_url)
            ? <img src={p.visual_preview_url || p.visual_url} alt="" loading="lazy" className="w-10 h-10 rounded-lg object-cover border border-neutral-100 shrink-0" />
            : <span className="w-10 h-10 rounded-lg flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ background: p.client_color || "#94A3B8" }}>{initials(p.client_name)}</span>}
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-[#0F172A] truncate">{p.client_name}</p>
            <p className="text-[10px] text-neutral-400 truncate">{p.title} · {p.format}{p.date ? ` · ${p.date.slice(8, 10)}/${p.date.slice(5, 7)}` : ""}</p>
          </div>
          <div className="flex items-center gap-2.5 text-[10px] text-neutral-500 shrink-0">
            <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{fmtN(p.reach)}</span>
            <span className="flex items-center gap-0.5"><Heart className="w-3 h-3 text-[#E1306C]" />{fmtN(p.likes)}</span>
            <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3 text-[#0369A1]" />{fmtN(p.comments)}</span>
          </div>
          <span className={`text-[9px] font-semibold rounded-full px-2 py-0.5 shrink-0 ${BADGE_STYLES[p.badge] || BADGE_STYLES["Régulier"]}`}>{p.badge}</span>
        </button>
      ))}
    </div>
  );
}

export function ObjectifsBars({ objectives, onEdit, testid }) {
  const defined = (objectives || []).filter((o) => o.target > 0);
  return (
    <div className="px-4 py-3 space-y-2.5" data-testid={testid}>
      {!defined.length && (
        <p className="text-[12px] text-neutral-400 py-2">Aucun objectif défini.{onEdit ? " Cliquez sur « Définir » pour configurer les objectifs." : ""}</p>
      )}
      {defined.map((o) => (
        <div key={o.id} data-testid={`${testid}-${o.id}`}>
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[11px] font-medium text-[#475569]">{o.label}</p>
            <p className="text-[11px] font-bold text-[#0F172A]">{o.pct} %</p>
          </div>
          <div className="h-2 rounded-full bg-neutral-100 mt-1 overflow-hidden">
            <div className={`h-full rounded-full ${o.pct >= 100 ? "bg-[#15803D]" : "bg-[#0369A1]"}`} style={{ width: `${Math.min(100, o.pct || 0)}%` }} />
          </div>
          <p className="text-[10px] text-neutral-400 mt-0.5">
            {o.fmt === "pct" ? `${String(o.current).replace(".", ",")} % / ${o.target} %`
              : o.fmt === "signed" ? `+${fmtN(o.current)} / +${fmtN(o.target)}`
              : `${fmtN(o.current)} / ${fmtN(o.target)}`}
          </p>
        </div>
      ))}
    </div>
  );
}

export function DemoBadge({ demo, lastSync }) {
  if (!demo && lastSync) {
    return (
      <span className="text-[10px] text-neutral-400" data-testid="analyse-last-sync">
        Dernière synchronisation : {new Date(lastSync).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
      </span>
    );
  }
  if (demo) {
    return (
      <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 border bg-amber-50 text-amber-700 border-amber-200" data-testid="analyse-demo-badge">
        Données de démonstration
      </span>
    );
  }
  return null;
}

export const INSIGHT_ICONS = { format: TrendingUp, clock: Clock, heart: Heart, target: Target, users: Users };
export const ACTIVITY_ICONS = { publication: ImageIcon, demande: Inbox, contenu: FolderOpen, rapport: FileText };
